import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    STANDARD_POPUP_FIELDS,
    getPopupMetadataFields,
    MAX_POPUP_METADATA_FIELDS,
    validatePopupFields,
} from '../../../utils/discoverLayerFilterSchema';
import './LayerWizardStepPopup.scss';

/**
 * Wizard Step 3 — Popup visible fields with ordering.
 *
 * Allows selecting which fields appear in a marker popup and their order.
 * XSS-safe: all values are rendered as text nodes only.
 */
export default function LayerWizardStepPopup({
    objectType,
    popupFields,
    popupHideEmpty,
    onToggleField,
    onMoveField,
    onHideEmptyChange,
    onValidityChange,
}) {
    const { t } = useTranslation();

    const metadataFields = useMemo(
        () => getPopupMetadataFields(objectType),
        [objectType],
    );

    const validationError = useMemo(
        () => validatePopupFields(popupFields, objectType),
        [popupFields, objectType],
    );

    const isValid = validationError === null;

    useEffect(() => {
        onValidityChange(isValid);
    }, [isValid, onValidityChange]);

    const handleToggle = useCallback((fieldId) => {
        onToggleField(fieldId);
    }, [onToggleField]);

    const handleHideEmptyToggle = useCallback((e) => {
        onHideEmptyChange(e.target.checked);
    }, [onHideEmptyChange]);

    const errorMessage = validationError
        ? t(`discoverPage.layerWizard.popup.errors.${validationError}`, {
            max: MAX_POPUP_METADATA_FIELDS,
            defaultValue: validationError,
        })
        : null;

    return (
        <div className="layer-wizard-popup">
            <h3 className="layer-wizard-popup__title">
                {t('discoverPage.layerWizard.popup.sectionTitle', 'Visible popup fields')}
            </h3>

            <p className="layer-wizard-popup__hint">
                {t('discoverPage.layerWizard.popup.selectedOrderHint', 'Fields appear in this order inside the popup.')}
            </p>

            {/* Standard fields */}
            <fieldset className="layer-wizard-popup__group">
                <legend className="layer-wizard-popup__group-label">
                    {t('discoverPage.layerWizard.popup.standardFieldsLabel', 'Standard fields')}
                </legend>
                {STANDARD_POPUP_FIELDS.map((field) => {
                    const checked = popupFields.includes(field.id);
                    return (
                        <FieldCheckboxRow
                            key={field.id}
                            fieldId={field.id}
                            label={t(field.labelKey, field.labelDefault)}
                            checked={checked}
                            index={popupFields.indexOf(field.id)}
                            totalSelected={popupFields.length}
                            onToggle={handleToggle}
                            onMove={onMoveField}
                            t={t}
                        />
                    );
                })}
            </fieldset>

            {/* Metadata fields */}
            {metadataFields.length > 0 && (
                <fieldset className="layer-wizard-popup__group">
                    <legend className="layer-wizard-popup__group-label">
                        {t('discoverPage.layerWizard.popup.metadataFieldsLabel', 'Metadata fields')}
                    </legend>
                    {metadataFields.map((field) => {
                        const checked = popupFields.includes(field.id);
                        return (
                            <FieldCheckboxRow
                                key={field.id}
                                fieldId={field.id}
                                label={t(field.labelKey, field.labelDefault)}
                                checked={checked}
                                index={popupFields.indexOf(field.id)}
                                totalSelected={popupFields.length}
                                onToggle={handleToggle}
                                onMove={onMoveField}
                                t={t}
                            />
                        );
                    })}
                </fieldset>
            )}

            {/* Hide empty toggle */}
            <label className="layer-wizard-popup__hide-empty">
                <input
                    type="checkbox"
                    checked={popupHideEmpty}
                    onChange={handleHideEmptyToggle}
                />
                <span>{t('discoverPage.layerWizard.popup.hideEmpty', 'Hide empty fields')}</span>
            </label>

            {errorMessage && (
                <p className="layer-wizard-popup__error" role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
}

LayerWizardStepPopup.propTypes = {
    objectType: PropTypes.string.isRequired,
    popupFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    popupHideEmpty: PropTypes.bool.isRequired,
    onToggleField: PropTypes.func.isRequired,
    onMoveField: PropTypes.func.isRequired,
    onHideEmptyChange: PropTypes.func.isRequired,
    onValidityChange: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// FieldCheckboxRow (internal)
// ---------------------------------------------------------------------------

function FieldCheckboxRow({ fieldId, label, checked, index, totalSelected, onToggle, onMove, t }) {
    const handleChange = useCallback(() => {
        onToggle(fieldId);
    }, [fieldId, onToggle]);

    const handleMoveUp = useCallback(() => {
        onMove(fieldId, 'up');
    }, [fieldId, onMove]);

    const handleMoveDown = useCallback(() => {
        onMove(fieldId, 'down');
    }, [fieldId, onMove]);

    const canMoveUp = checked && index > 0;
    const canMoveDown = checked && index >= 0 && index < totalSelected - 1;

    return (
        <div className="layer-wizard-popup__field-row">
            <label className="layer-wizard-popup__field-label">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={handleChange}
                    className="layer-wizard-popup__field-checkbox"
                />
                <span className="layer-wizard-popup__field-name">{label}</span>
            </label>

            {checked && (
                <div className="layer-wizard-popup__field-order">
                    <button
                        type="button"
                        className="layer-wizard-popup__move-btn"
                        onClick={handleMoveUp}
                        disabled={!canMoveUp}
                        aria-label={t('discoverPage.layerWizard.actions.moveUpAria', {
                            field: label,
                            defaultValue: 'Move {{field}} up',
                        })}
                    >
                        &#x25B2;
                    </button>
                    <button
                        type="button"
                        className="layer-wizard-popup__move-btn"
                        onClick={handleMoveDown}
                        disabled={!canMoveDown}
                        aria-label={t('discoverPage.layerWizard.actions.moveDownAria', {
                            field: label,
                            defaultValue: 'Move {{field}} down',
                        })}
                    >
                        &#x25BC;
                    </button>
                </div>
            )}
        </div>
    );
}

FieldCheckboxRow.propTypes = {
    fieldId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    checked: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
    totalSelected: PropTypes.number.isRequired,
    onToggle: PropTypes.func.isRequired,
    onMove: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};
